/* ============================================================
   AI Product Search — gofor360
   Autocomplete dropdown over Shopify predictive search.
   Attaches to any chat input; shows product suggestions live.

   Usage:
     AIProductSearch.attach(inputElement, { onSelect: fn });
   ============================================================ */

window.AIProductSearch = (function () {
  'use strict';

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 180;
  const LIMIT = 5;
  const cache = new Map();

  // i18n (falls back to Italian if the module isn't loaded)
  const i18n = window.AII18n || { locale: 'it', isPrimary: true, t: () => '', url: (p) => p };
  const T = (k, fallback) => i18n.t(k) || fallback;

  const MONEY_LOCALE = { it: 'it-IT', en: 'en-IE', de: 'de-DE', es: 'es-ES' };

  function money(v) {
    const n = parseFloat(v);
    if (isNaN(n)) return '';
    const loc = MONEY_LOCALE[i18n.locale] || 'it-IT';
    return n.toLocaleString(loc, { style: 'currency', currency: 'EUR' });
  }

  function cleanUrl(u) {
    // strip Shopify predictive-search tracking params
    return (u || '').split('?')[0];
  }

  async function search(query) {
    const key = query.toLowerCase().trim();
    if (cache.has(key)) return cache.get(key);

    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', String(LIMIT));
    params.set('resources[options][unavailable_products]', 'last');

    try {
      // Locale-aware endpoint → returns translated titles and /en /de /es URLs
      const res = await fetch(i18n.url('/search/suggest.json') + '?' + params.toString());
      if (!res.ok) return [];
      const data = await res.json();
      const products = (data.resources?.results?.products || []).map(p => ({
        title: p.title,
        handle: p.handle,
        url: cleanUrl(p.url) || i18n.url('/products/' + p.handle),
        price: p.price,
        compareAt: p.compare_at_price,
        image: p.featured_image?.url || p.image || '',
        available: p.available !== false
      }));
      cache.set(key, products);
      return products;
    } catch (e) {
      console.warn('[ai-product-search]', e);
      return [];
    }
  }

  function buildDropdown() {
    const el = document.createElement('div');
    el.className = 'ai-search-dropdown';
    el.setAttribute('role', 'listbox');
    el.hidden = true;
    return el;
  }

  function renderItems(dropdown, products, query, onSelect) {
    dropdown.innerHTML = '';

    if (!products.length) {
      dropdown.hidden = true;
      return;
    }

    const header = document.createElement('div');
    header.className = 'ai-search-dropdown__header';
    header.textContent = T('searchHeader', 'Prodotti');
    dropdown.appendChild(header);

    products.forEach((p, i) => {
      const item = document.createElement('a');
      item.className = 'ai-search-item';
      item.href = p.url;
      item.setAttribute('role', 'option');
      item.dataset.index = String(i);

      if (p.image) {
        const img = document.createElement('img');
        img.className = 'ai-search-item__img';
        img.src = p.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
        img.alt = '';
        img.loading = 'lazy';
        img.onerror = () => { img.style.visibility = 'hidden'; };
        item.appendChild(img);
      } else {
        const ph = document.createElement('span');
        ph.className = 'ai-search-item__img';
        item.appendChild(ph);
      }

      const meta = document.createElement('span');
      meta.className = 'ai-search-item__meta';

      const t = document.createElement('span');
      t.className = 'ai-search-item__title';
      t.innerHTML = highlight(p.title, query);
      meta.appendChild(t);

      const pr = document.createElement('span');
      pr.className = 'ai-search-item__price';
      if (p.compareAt && parseFloat(p.compareAt) > parseFloat(p.price)) {
        const del = document.createElement('del');
        del.textContent = money(p.compareAt);
        pr.appendChild(del);
        pr.appendChild(document.createTextNode(' '));
      }
      pr.appendChild(document.createTextNode(money(p.price)));
      meta.appendChild(pr);

      item.appendChild(meta);

      const arrow = document.createElement('span');
      arrow.className = 'ai-search-item__arrow';
      arrow.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';
      item.appendChild(arrow);

      item.addEventListener('click', (e) => {
        if (typeof onSelect === 'function') {
          const handled = onSelect(p, e);
          if (handled === false) e.preventDefault();
        }
      });

      dropdown.appendChild(item);
    });

    // "Ask the AI instead" footer
    const ask = document.createElement('button');
    ask.type = 'button';
    ask.className = 'ai-search-dropdown__ask';
    ask.innerHTML = '<span></span> <strong></strong>';
    ask.querySelector('span').textContent = T('askAssistant', "Chiedi all'assistente:");
    ask.querySelector('strong').textContent = '"' + query + '"';
    ask.addEventListener('click', () => {
      dropdown.hidden = true;
      const form = dropdown._input?.closest('form');
      if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    });
    dropdown.appendChild(ask);

    dropdown.hidden = false;
  }

  function highlight(text, q) {
    const esc = (s) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    if (!q) return esc(text);
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return esc(text);
    return esc(text.slice(0, idx)) + '<mark>' + esc(text.slice(idx, idx + q.length)) + '</mark>' + esc(text.slice(idx + q.length));
  }

  function attach(input, opts) {
    if (!input || input._aiSearchAttached) return;
    input._aiSearchAttached = true;
    opts = opts || {};

    // Wrap so the dropdown can be absolutely positioned
    const anchor = opts.anchor || input.closest('form') || input.parentElement;
    if (getComputedStyle(anchor).position === 'static') {
      anchor.style.position = 'relative';
    }

    const dropdown = buildDropdown();
    dropdown._input = input;
    anchor.appendChild(dropdown);

    let timer = null;
    let activeIndex = -1;
    let currentProducts = [];

    // Section wrapper — used to hide sibling UI (chips) while results are open
    const section = input.closest('.ai-hero') || input.closest('.ai-chat-page');

    function setOpen(isOpen) {
      dropdown.hidden = !isOpen;
      if (section) section.classList.toggle('ai-search-open', isOpen);
    }

    function close() {
      setOpen(false);
      activeIndex = -1;
      updateActive();
    }

    function updateActive() {
      const items = dropdown.querySelectorAll('.ai-search-item');
      items.forEach((it, i) => it.classList.toggle('is-active', i === activeIndex));
      if (activeIndex >= 0 && items[activeIndex]) {
        items[activeIndex].scrollIntoView({ block: 'nearest' });
      }
    }

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(timer);
      if (q.length < MIN_CHARS) { close(); return; }
      timer = setTimeout(async () => {
        const products = await search(q);
        currentProducts = products;
        activeIndex = -1;
        renderItems(dropdown, products, q, opts.onSelect);
        if (section) section.classList.toggle('ai-search-open', !dropdown.hidden);
      }, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', (e) => {
      if (dropdown.hidden) return;
      const items = dropdown.querySelectorAll('.ai-search-item');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        updateActive();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, -1);
        updateActive();
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && currentProducts[activeIndex]) {
          e.preventDefault();
          const p = currentProducts[activeIndex];
          if (typeof opts.onSelect === 'function') {
            const handled = opts.onSelect(p, e);
            if (handled !== false) window.location.href = p.url;
          } else {
            window.location.href = p.url;
          }
          close();
        } else {
          // let the form submit → AI chat
          close();
        }
      } else if (e.key === 'Escape') {
        close();
      }
    });

    input.addEventListener('blur', () => setTimeout(close, 180));
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_CHARS && currentProducts.length) {
        setOpen(true);
      }
    });

    document.addEventListener('click', (e) => {
      if (!anchor.contains(e.target)) close();
    });
  }

  return { attach, search };
})();
